<template>
    <v-dialog v-model="dialog" width="800" :fullscreen="isMobileOrTablet()" persistent>
        <v-card class="rounded-lg" style="display: flex; flex-direction: column; height: 100%;">
            <v-card-title style="background-color: #1a5b0f !important; display: flex; justify-content: space-between; align-items: center;">
                <div>
                    <span class="font-weight-bold text-h6 text-white">{{ dialog_title }}</span>
                    <div class="text-caption text-white">{{ dialog_sub_title }}</div>
                </div>
                <v-btn v-if="loading == false" flat icon color="darkGreen" @click="closeDialog()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="overflow-y: auto; max-height: calc(100vh - 100px);">
                <v-form ref="form" class="mt-2">
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="so_number"
                                :rules="[formRules.required]"
                                label="Special Order Number"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                :loading="searchLoading"
                                >
                            </v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="category"
                                :rules="[formRules.required]"
                                label="Category"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                :items="categoryData"
                                item-title="name"
                                item-value="value"
                                >
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="fund_type"
                                :rules="[formRules.required]"
                                label="Fund Type"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                :items="fundTypeData"
                                item-title="name"
                                item-value="value"
                                @update:model-value="onFundTypeChange"
                                >
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="fund_source"
                                :rules="[formRules.required]"
                                label="Fund Source"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                :items="fundSourceOptions"
                                item-title="name"
                                item-value="value"
                                :disabled="fund_type === 'internal'"
                                >
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea
                                v-model="title"
                                :rules="[formRules.required]"
                                label="Title"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                >
                            </v-textarea>
                        </v-col>
                        <v-col cols="12" v-if="searchResults.length">
                        <v-card class="pa-2" elevation="2" max-height="300" style="overflow-y:auto;">
                            <v-list density="compact">
                            <v-list-item
                                v-for="item in searchResults"
                                :key="item.id"
                                @click="selectSo(item)"
                                style="cursor:pointer"
                            >
                                <v-list-item-title class="font-weight-bold text-body-2">
                                {{ item.so_number }}
                                </v-list-item-title>
                                <div class="text-caption">
                                {{ item.title }}
                                </div>
                            </v-list-item>
                            </v-list>
                        </v-card>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-date-input
                                v-model="start_date"
                                prepend-icon=""
                                prepend-inner-icon="mdi-calendar"
                                :rules="[formRules.required]"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                label="Start Date"
                            ></v-date-input>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-date-input
                                v-model="end_date"
                                prepend-icon=""
                                prepend-inner-icon="mdi-calendar"
                                :rules="[formRules.required, formRules.endAfterStart]"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                label="End Date"
                            ></v-date-input>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="remarks"
                                label="Remarks"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                >
                            </v-text-field>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-3" style="margin-top: auto;">
                <v-spacer></v-spacer>
                <v-btn class="mr-1" :disabled="loading" text="Cancel" variant="outlined" elevation="1" @click="closeDialog()"></v-btn>
                <v-btn class="mr-1" :loading="loading" text="Save" variant="elevated" elevation="1" color="darkGreen" @click="validateForm()"></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'

export default defineComponent({
    emits: ["refresh"],
    data () {
        return {
            dialog: false,
            dialog_title: null,
            dialog_sub_title: null,
            action: null,
            loading: false,

            id: null,
            so_number: null,
            title: null,
            start_date: null,
            end_date: null,
            category: null,
            fund_type: null,
            fund_source: null,
            remarks: null,

            searchResults: [],
            searchLoading: false,
            searchTimeout: null,
            isSelecting: false,

            categoryData: [
                { name: 'Research', value: 'Res' },
                { name: 'Extension', value: 'Ext' },
            ],

            fundTypeData: [
                { name: 'Internal', value: 'internal' },
                { name: 'External', value: 'external' },
            ],

            fundSourceData: [
                { name: 'Local', value: 'local' },
                { name: 'Regional', value: 'regional' },
                { name: 'National', value: 'national' },
                { name: 'International', value: 'international' },
            ],

            formRules: {
                required: v => !!v || 'Required.',
                endAfterStart: v => {
                    if (!v || !this.start_date) return true
                    return new Date(v) > new Date(this.start_date) || 'End Date must be greater than Start Date'
                }
            }
        }
    },
    computed: {
        fundSourceOptions() {
            if (this.fund_type === 'internal') {
                return this.fundSourceData.filter(item => item.value === 'local')
            }

            if (this.fund_type === 'external') {
                return this.fundSourceData.filter(item => item.value !== 'local')
            }

            return this.fundSourceData
        }
    },
    watch: {
        so_number(val) {
            if (this.isSelecting) return

            if (!val || val.length < 3) {
            this.searchResults = []
            return
            }

            if (!val) {
            this.searchResults = []
            return
            }

            clearTimeout(this.searchTimeout)

            this.searchTimeout = setTimeout(() => {
            this.searchSoNumber(val)
            }, 400)
        }
    },
    methods: {
        initAdd(){
            this.dialog = true
            this.dialog_title = 'RDE Load Title'
            this.dialog_sub_title = 'Add new rde load title'
            this.action = 'add'
        },
        initEdit(data){
            this.dialog = true
            this.dialog_title = 'RDE Load Title'
            this.dialog_sub_title = 'Update rde load title'
            this.action = 'edit'

            this.id = data.id
            this.so_number = data.so_number
            this.category = data.category,
            this.fund_source = data.fund_source,
            this.fund_type = data.fund_type,
            this.title = data.title
            this.start_date = new Date(data.start_date)
            this.end_date = new Date(data.end_date)
            this.remarks = data.remarks
        },
        onFundTypeChange(val){
            if (val === 'internal') {
                this.fund_source = 'local'
            } else if (val === 'external') {
                this.fund_source = null
            } else {
                this.fund_source = null
            }
        },
        async searchSoNumber(val){
            this.searchLoading = true
            const search = this.axiosCall('/rep-title/searchBySoNumber/' + val + '/' + this.$store.state.semester_schoolyear.id, 'get', null, true)
            search.then((res)=>{
                this.searchResults = res.data
                this.searchLoading = false
            }).catch((error)=>{
                this.searchLoading = false
                console.log(error)
            })
        },
        selectSo(item) {
            this.isSelecting = true

            this.so_number = item.so_number
            this.title = item.title
            this.category = item.category
            this.start_date = item.start_date ? new Date(item.start_date) : null
            this.end_date = item.end_date ? new Date(item.end_date) : null
            this.remarks = item.remarks
            this.id = item.id

            this.searchResults = []

            setTimeout(() => {
                this.isSelecting = false
            }, 0)
        },
        async validateForm(){
            const { showSnackbar } = useSnackbar()
            const { valid } = await this.$refs.form.validate()

            if(valid){
                this.loading = true
                let formData = {
                    id: this.id,
                    so_number: this.so_number,
                    title: this.title,
                    start_date: this.start_date,
                    end_date: this.end_date,
                    category: this.category,
                    remarks: this.remarks,
                    fund_source: this.fund_source,
                    fund_type: this.fund_type,
                    semester_schoolyear: this.$store.state.semester_schoolyear.id
                }

                const token = localStorage.getItem('aums-token')

                if(this.action == 'add')[
                    axios.post(process.env.VUE_APP_SERVER_URL + '/rep-title', formData,
                    { headers:
                        { 
                            'Authorization': `Bearer ${token}`,
                        }
                    }
                    ).then((res)=>{
                        if(res.data.status == 200){
                            this.loading = false
                            this.$emit('refresh')
                            showSnackbar('REP Load Title successfully added!')
                            this.$refs.form.reset()
                            this.dialog = false
                        }
                    }).catch((error)=>{
                        if(error.response.status == 401){
                            this.loading = false
                            showSnackbar('Token expired! Logging out..')

                            localStorage.removeItem("vuex");
                            localStorage.removeItem("aums-token");
                            window.location.reload();
                        }
                        else{
                            console.log(error)
                        }
                    })
                ]
                else if(this.action == 'edit'){
                    axios.patch(process.env.VUE_APP_SERVER_URL + '/rep-title', formData,
                    { headers:
                        { 
                            'Authorization': `Bearer ${token}`,
                        }
                    }
                    ).then((res)=>{
                        if(res.data.status == 200){
                            this.loading = false
                            this.$emit('refresh')
                            showSnackbar('REP Load Title successfully updated!')
                            this.$refs.form.reset()
                            this.dialog = false
                        }
                    }).catch((error)=>{
                        if(error.response.status == 401){
                            this.loading = false
                            showSnackbar('Token expired! Logging out..')

                            localStorage.removeItem("vuex");
                            localStorage.removeItem("aums-token");
                            window.location.reload();
                        }
                        else{
                            console.log(error)
                        }
                    })
                }
            }
        },
        closeDialog(){
            this.$refs.form.reset()
            this.dialog = false
        }
    }
})
</script>

<style scoped>

</style>